import ScrollModal from "@/components/common/scroll-modal";
import {
  Avatar,
  AvatarFallback,
  Button,
  Heading,
  Icon,
} from "@elaniintc/goes-design-system";
import { type Fields } from "../form-correction-table/index";
import { getDataTypeIcon } from "../form-correction-table/utils";
import dayjs from "dayjs";
import { buildFieldChangeHistory } from "../serialization/serialization";
import type { FormDetailResponse } from "@/types/entities/formality-request";
import FieldValue from "./field-value";

interface FieldHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  fieldSelected: Fields;
  applicant?: {
    fullName: string;
    date?: string | null;
  };
  formAssigment?: FormDetailResponse["form_assignments"][number];
}

const FieldHistoryModal = ({
  isOpen,
  onClose,
  fieldSelected,
  applicant,
  formAssigment,
}: FieldHistoryProps) => {
  const fieldHistory = buildFieldChangeHistory(fieldSelected, formAssigment);

  const formatDate = (date: Date) => {
    const d = dayjs(date);

    const day = d.format("D");
    const month = d.format("MMM");
    const rest = d.format(", YYYY | HH:mm");

    return `el ${day} de ${month.charAt(0).toUpperCase() + month.slice(1)}${rest}`;
  };

  return (
    <ScrollModal
      isOpen={isOpen}
      onClose={onClose}
      modalContentClassName="gap-0 max-h-[680px]"
      modalChildrenClassName="rounded-b-2xl"
      header={
        <div className="px-8 py-3">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <Avatar className="shrink-0" shape="square" size="2xs">
                <AvatarFallback color="neutral" shape="square">
                  {getDataTypeIcon(fieldSelected.type)}
                </AvatarFallback>
              </Avatar>

              <div className="text-center text-h2! font-semibold text-heading">
                <Heading variant="h5">{fieldSelected.label}</Heading>
              </div>
            </div>
            <Button
              variant="quiet"
              onClick={onClose}
              className="h-9 w-9 justify-center items-center gap-2 rounded-lg border border-neutral shadow-sm"
              data-testid="close-new-notification-modal-button"
            >
              <Icon type="far" name="times" className="text-sm text-body" />
            </Button>
          </div>
        </div>
      }
    >
      <div className="px-8 pt-8">
        {/* Empty State */}

        {fieldHistory.length ? (
          <>
            {fieldHistory.map((field) => {
              return (
                <div
                  key={`change-${field.oldFieldId}-${field.newFieldId}-${field.changedAt.getTime()}`}
                  className="flex flex-col gap-8 border border-neutral-quiet rounded-xl mb-8"
                >
                  <div className="p-3 flex flex-col gap-3">
                    <div className="flex items-center gap-1">
                      <Avatar className="shrink-0" size="2xs">
                        <AvatarFallback color="neutral">
                          <Icon type="fa" size="xs" name="file-pen" />
                        </AvatarFallback>
                      </Avatar>

                      <div className="font-semibold text-heading">
                        <span>Revisión {field.order}</span>
                      </div>
                    </div>

                    <div className="rounded-lg bg-neutral-object-accent-silent border border-neutral-quiet p-2 flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <Icon
                          type="fa"
                          name="comment"
                          className="text-warning-quiet"
                        />
                        <span className="text-warning">Comentario: </span>
                      </div>

                      <span className="text-body whitespace-pre-line text-justify wrap-break-word min-w-0 overflow-hidden">
                        {field.comment || "No hay comentario para este cambio."}
                      </span>

                      <div className="flex items-center gap-1 mb-1 text-sm">
                        <span className="text-body">Por</span>

                        <Avatar className="shrink-0" size="xs">
                          <AvatarFallback color="action">
                            {field?.author?.substring(0, 1).toUpperCase() ||
                              "U"}
                          </AvatarFallback>
                        </Avatar>

                        <span className="text-action">{field?.author}</span>

                        <span className="text-body">
                          {formatDate(field?.changedAt)}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <span className="text-body-loud font-bold">
                        Valor que cambió
                      </span>
                      <div className="flex flex-col gap-2 rounded-lg bg-neutral-object-accent-silent border border-neutral-quiet p-2">
                        <div className="flex items-center gap-2">
                          <Icon
                            type="fa"
                            name="arrow-down"
                            className="text-body-quiet"
                          />
                          <div className="flex flex-col gap-1 min-w-0 overflow-hidden">
                            <FieldValue
                              field={fieldSelected}
                              value={field.oldValue}
                              filledFieldId={field.oldFieldId}
                              isOldValue
                            />
                            <FieldValue
                              field={fieldSelected}
                              filledFieldId={field.newFieldId}
                              value={field.newValue}
                            />
                          </div>
                        </div>

                        <div className="flex items-center gap-1 mb-1 text-sm">
                          <span className="text-body">Por</span>

                          <Avatar className="shrink-0" size="xs">
                            <AvatarFallback color="action">
                              {applicant?.fullName
                                .substring(0, 1)
                                .toUpperCase() || "U"}
                            </AvatarFallback>
                          </Avatar>

                          <span className="text-action">
                            {applicant?.fullName}
                          </span>

                          <span className="text-body">
                            {formatDate(
                              applicant?.date
                                ? new Date(applicant.date)
                                : new Date(),
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          <div className="h-full min-h-130 flex flex-col justify-center items-center gap-4 p-8">
            <Heading variant="h2" className="text-center">
              No hay cambios dentro de este campo.
            </Heading>
            <span className="text-body text-center">
              Todavía no se han recibido comentarios ni generado cambios sobre
              este campo.
            </span>
          </div>
        )}
      </div>
    </ScrollModal>
  );
};

export default FieldHistoryModal;
